/**
 * Rapprochement d'un virement ÉMIS avec la note de crédit qu'il rembourse.
 *
 * Le miroir de `matching.ts`, et volontairement un module séparé. La raison
 * n'est pas de principe, elle est dans quatre endroits du code réel :
 *
 * 1. **Les formules sont opposées.** `resteDu` (`matching.ts:213`) calcule
 *    `max(0, brut − notes − affecté)` : ce qu'une facture RÉCLAME encore. Ce
 *    qu'elle peut RENDRE est `max(0, affecté − (brut − notes))`. Ce ne sont pas
 *    deux paramétrages d'une même fonction, ce sont deux fonctions contraires,
 *    et c'est le cœur de `affecter()`.
 * 2. **La clé d'index n'a pas la même forme** — voir `PIÈGE 1` ci-dessous.
 * 3. **Trois des huit raisons de file manuelle changent de sens.**
 *    `invoice_cancelled` n'a aucun miroir (`credit_notes.status` ne vaut que
 *    `issued` ou `sent`) ; `foreign_communication` s'inverse — une
 *    communication structurée sur un virement QUE NOUS AVONS ÉMIS est la nôtre,
 *    et si elle nous est inconnue c'est une anomalie, pas « un autre système ».
 * 4. Ce qui resterait partagé est un squelette de boucle de trente lignes, pour
 *    le prix de mettre en risque un moteur couvert par 391 lignes de test.
 *
 * ## La règle, en une phrase
 *
 * **Le montant décide.** Un virement dont le montant coïncide exactement avec
 * la ou les notes citées se rapproche tout seul. Tout le reste est proposé à
 * un humain et n'est jamais appliqué.
 *
 * ## Ce que le moteur n'exige PAS, et c'est délibéré
 *
 * Il **n'exige pas que la facture paraisse payée.** Le rapprochement des
 * paiements entrants n'est pas terminé — 5 528 paiements n'ont encore aucune
 * ligne d'allocation — donc une note de crédit peut parfaitement rembourser de
 * l'argent que le parent avait versé et que nous n'avons pas encore encodé.
 *
 * Mesuré le 12 septembre 2026 : **18 des 36 notes réellement remboursées, soit
 * la moitié, portent sur une facture à `allocated_cents = 0`.** Un moteur qui
 * exigerait un trop-perçu visible ne proposerait donc jamais rien, et se
 * tromperait une fois sur deux en refusant.
 *
 * ## Les trois pièges, mesurés en production
 *
 * **PIÈGE 1 — la clé d'une note n'est PAS ses sept chiffres.**
 * `invoiceDigits` tient parce que les sept chiffres d'une facture sont
 * globalement uniques et portent l'année. La séquence des notes, elle, repart
 * chaque année : 1 007 notes, 504 en `25`, 502 en `26`, pour seulement 590
 * suffixes de cinq chiffres distincts. Et surtout :
 *
 * > **854 des 1 007 numéros de note (84,8 %) collisionnent, sur sept chiffres,
 * > avec un numéro de facture réel — et 851 de ces 854, soit 99,6 %, désignent
 * > une AUTRE FAMILLE.**
 *
 * `NC-25-00182` (84,00 €) et `CDV-WBK-2500182` (30,00 €) se réduisent tous deux
 * à `2500182`. Une recherche par chiffres sur un numéro de note rendrait donc
 * presque toujours la facture de quelqu'un d'autre. **La clé est le numéro
 * canonique complet `NC-AA-NNNNN`**, jamais un suffixe.
 *
 * **PIÈGE 2 — la troncature frappe plus fort à la sortie.** Le champ
 * communication CODA fait 53 caractères, et le numéro de note s'écrit APRÈS
 * celui de la facture : c'est donc lui qui saute. Onze remboursements sur 45
 * ont perdu au moins un numéro. Le moteur distingue l'ABSENCE de note de son
 * AMPUTATION : un texte finissant par `NC-26-0014` n'est pas un texte sans note.
 *
 * **PIÈGE 3 — les deux références peuvent se contredire.** Cas réel :
 * `NC-25-00589 - CDV-MC-2600396 REMBOURSEMENT ANNULATIO`, où la note appartient
 * à `CDV-ISA-2501488` — **une autre famille**. Le moteur confronte les deux et
 * lève la main ; il ne choisit jamais.
 */

import type { CodaMovement } from './coda';
import { allLegacyRefs, invoiceTag } from './matching';
import { normalizeReference } from './communication';

/**
 * Une note de crédit, telle que le moteur a besoin de la connaître.
 *
 * `id` est nul quand la note est **citée mais absente de la base** : c'est le
 * cas de tout ce qui a été traité au tableur pendant l'année sans application.
 * L'architecture décide que le numéro est conservé quand même — on ne perd pas
 * ce que l'administration a écrit sous prétexte qu'on ne sait pas le relier.
 */
export interface CreditNoteRef {
  id: string | null;
  /** Forme canonique `NC-AA-NNNNN`. C'est la clé. */
  number: string;
  amountCents: number;
  invoiceId: string | null;
  invoiceNumber: string | null;
  /** La famille à qui la note appartient. Sert à détecter le PIÈGE 3. */
  userId: string | null;
  /** `issued` ou `sent` — aucune valeur n'est refusante aujourd'hui. */
  status: string;
  /** Déjà rendu en argent sur cette note. Porte l'invariant 2'. */
  refundedCents: number;
}

/**
 * Une demande de remboursement de provision, en attente du virement.
 *
 * Elle porte **sa propre communication structurée**, préfixe `900`, émise par
 * nous au moment où la famille a demandé. C'est ce qui permet au virement
 * sortant de se rapprocher tout seul, sans annotation manuelle — contrairement
 * aux remboursements de notes de crédit, annotés à la main et lus par
 * expression régulière.
 */
export interface DemandeDeRemboursement {
  id: string;
  /** Les 12 chiffres, préfixe `900`. */
  communicationDigits: string;
  userId: string;
  amountCents: number;
  /** Déjà placé sur cette demande. */
  placedCents: number;
}

/** Une facture citée par un remboursement. */
export interface FactureCitee {
  id: string;
  invoiceNumber: string;
  userId: string | null;
}

export interface RefundIndex {
  /** Par numéro canonique complet. Jamais par chiffres — voir PIÈGE 1. */
  noteParNumero(numero: string): CreditNoteRef | undefined;
  factureParChiffres(chiffres: string): FactureCitee | undefined;
  /** Par les 12 chiffres de sa communication structurée. */
  demandeParCommunication(digits: string): DemandeDeRemboursement | undefined;
}

export function buildRefundIndex(
  notes: CreditNoteRef[],
  factures: FactureCitee[],
  demandes: DemandeDeRemboursement[] = [],
): RefundIndex {
  const parNumero = new Map<string, CreditNoteRef>();
  for (const n of notes) parNumero.set(normalizeReference(n.number), n);

  const parChiffres = new Map<string, FactureCitee>();
  for (const f of factures) {
    const d = normalizeReference(f.invoiceNumber).match(/(\d{7})$/);
    if (d) parChiffres.set(d[1], f);
  }

  const parCommunication = new Map<string, DemandeDeRemboursement>();
  for (const d of demandes) parCommunication.set(d.communicationDigits, d);

  return {
    noteParNumero: (numero) => parNumero.get(normalizeReference(numero)),
    factureParChiffres: (chiffres) => parChiffres.get(chiffres),
    demandeParCommunication: (digits) => parCommunication.get(digits),
  };
}

/** Pourquoi un virement émis demande une décision humaine. */
export type RefundManualReason =
  /** Aucune référence exploitable : ni note, ni facture. */
  | 'no_reference'
  /** La forme dit une dépense qui n'appartient pas aux familles. */
  | 'out_of_scope_pattern'
  /**
   * Un numéro de note a été lu, la note est absente de la base.
   *
   * **Neutre, et non un échec.** C'est le cas normal de l'année traitée au
   * tableur. Le lien est conservé par numéro ; il se résoudra le jour où la
   * note existera.
   */
  | 'unknown_credit_note'
  /** Une référence de facture a été lue, aucune facture ne lui correspond. */
  | 'unknown_reference'
  /** Cette note a déjà été remboursée en entier. Invariant 2'. */
  | 'credit_note_already_refunded'
  /** La note citée et la facture citée n'appartiennent pas à la même famille. */
  | 'references_contradictoires'
  /**
   * Le montant ne coïncide pas avec ce qui est cité.
   *
   * C'est la raison la plus fréquente et elle n'est pas une erreur : un
   * remboursement partiel, un groupement, ou une communication coupée avant la
   * deuxième note produisent tous un écart. Un humain tranche.
   */
  | 'montant_discordant'
  /** Une note a été citée, mais la communication est coupée à 53 caractères. */
  | 'note_tronquee'
  /** Communication structurée sur un virement que nous avons émis, et inconnue. */
  | 'own_communication_unknown'
  /** Une part du virement reste sans emploi après les liens proposés. */
  | 'surplus';

/** Ce qu'un virement émis solde. */
export type RefundTarget = 'credit_note' | 'invoice' | 'provision' | 'out_of_scope';

export interface ProposedRefundLink {
  target: RefundTarget;
  amountCents: number;
  /** Conservé même quand `creditNoteId` est nul. */
  creditNoteNumber?: string;
  creditNoteId?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  /** La demande que ce lien solde, pour une cible `provision`. */
  refundRequestId?: string | null;
  provisionUserId?: string | null;
  method: 'structured' | 'credit_note_number' | 'legacy_reference' | 'manual';
}

export interface RefundPlan {
  /** La référence bancaire : clé d'idempotence de l'import. */
  externalRef: string;
  amountCents: number;
  valueDate: string;
  /**
   * Les liens **appliqués automatiquement**. Non vide seulement quand le
   * montant coïncide : c'est toute la règle.
   */
  links: ProposedRefundLink[];
  /**
   * Les liens **proposés, jamais appliqués**. L'écran les montre, un humain
   * confirme. Même posture que `classifyOutOfScope` côté entrant.
   */
  aConfirmer: ProposedRefundLink[];
  /** Ce qui reste sans emploi après les liens appliqués. */
  leftoverCents: number;
  manualReasons: RefundManualReason[];
  /** La famille, déduite de la note puis de la facture. Alimente `refunds.user_id`. */
  userId: string | null;
  /** Catégorie de dépense hors familles, reconnue à la forme. */
  outOfScope?: OutgoingMatch;
}

/* -------------------------------------------------------------------------- */
/*  Les dépenses qui ne reviennent pas à une famille                          */
/* -------------------------------------------------------------------------- */

export type OutgoingCategory =
  | 'volontariat'
  | 'frais_de_deplacement'
  | 'declaration_de_creance'
  | 'salaire'
  | 'frais_bancaires';

export interface OutgoingMatch {
  category: OutgoingCategory;
  /** Ce qui a déclenché la reconnaissance, pour que l'humain juge. */
  matched: string;
  label: string;
}

/*
  Motifs propres au SORTANT. `classifyOutOfScope` n'est pas réutilisable : son
  motif `sans_information` s'ancre sur `^virement|paiement` et ne verra jamais
  `VOLONTARIAT EXTRA CSSA 05-26`. Et mélanger les catégories entrantes
  (`partenaires`, `parascolaire_ed`) aux sortantes rendrait le journal illisible.

  Comme côté entrant : **proposé, jamais appliqué.** Un motif trop large
  enterrerait un vrai remboursement.
*/
const MOTIFS_SORTANTS: { category: OutgoingCategory; label: string; pattern: RegExp }[] = [
  {
    category: 'volontariat',
    label: 'Volontariat',
    // `VOLONTARIAT EXTRA CSSA 05-26` — la quasi-totalité des 58 virements émis
    // de juin-juillet 2026.
    pattern: /\bVOLONTARIAT\b/i,
  },
  {
    category: 'declaration_de_creance',
    label: 'Déclaration de créance',
    // `DECLARATION DE CREANCE JUIN 26`, forme réelle.
    pattern: /\bD[EÉ]CLARATION\s+DE\s+CR[EÉ]ANCE\b/i,
  },
  {
    category: 'frais_de_deplacement',
    label: 'Frais de déplacement',
    pattern: /\bFRAIS\s+DE\s+D[EÉ]PLACEMENT\b|\bNOTE\s+DE\s+FRAIS\b/i,
  },
  {
    category: 'salaire',
    label: 'Salaire ou rémunération',
    pattern: /\b(SALAIRE|REMUNERATION|R[EÉ]MUN[EÉ]RATION|PECULE|P[EÉ]CULE)\b/i,
  },
  {
    category: 'frais_bancaires',
    label: 'Frais bancaires',
    pattern: /\bFRAIS\s+(BANCAIRES|DE\s+TENUE)\b/i,
  },
];

/**
 * Reconnaît une dépense qui ne revient pas à une famille, ou rend `null`.
 *
 * Écartée du rapprochement automatique, **jamais du fichier** : elle est lue,
 * classée et comptée. C'est ce qui rendra un jour le total débit du relevé
 * rapprochable — `parseCoda` le calcule déjà et rien ne l'exploite.
 */
export function classifyOutgoing(communication: string | null | undefined): OutgoingMatch | null {
  const texte = (communication ?? '').trim();
  if (!texte) return null;
  for (const m of MOTIFS_SORTANTS) {
    const trouve = texte.match(m.pattern);
    if (trouve) return { category: m.category, matched: trouve[0].trim(), label: m.label };
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  La lecture des numéros de note                                            */
/* -------------------------------------------------------------------------- */

/** `NC-AA-NNNNN` sur chaîne normalisée — les tirets sont déjà tombés. */
const NOTE_COMPLETE = /NC(\d{2})(\d{5})/g;

/**
 * Un numéro de note AMPUTÉ par la troncature à 53 caractères.
 *
 * `NC`, `NC2`, `NC26`, `NC26001`… : le début d'un numéro sans ses cinq
 * chiffres. Distinguer ce cas de l'absence de note est ce qui empêche de
 * conclure « aucune note citée » sur huit remboursements réels.
 */
const NOTE_AMPUTEE = /NC(\d{0,6})$/;

/** Tous les numéros de note complets cités, dans l'ordre, sans doublon. */
export function allCreditNoteRefs(raw: string): string[] {
  const vus = new Set<string>();
  const refs: string[] = [];
  for (const m of normalizeReference(raw).matchAll(NOTE_COMPLETE)) {
    const numero = `NC-${m[1]}-${m[2]}`;
    if (vus.has(numero)) continue;
    vus.add(numero);
    refs.push(numero);
  }
  return refs;
}

/**
 * La communication cite-t-elle une note dont le numéro a été coupé ?
 *
 * Vrai seulement si le texte **se termine** par un début de numéro : c'est la
 * signature de la troncature, et elle ne peut pas se confondre avec un `NC`
 * écrit au milieu d'une phrase.
 */
export function citeUneNoteTronquee(raw: string): boolean {
  const normalise = normalizeReference(raw);
  const amputee = normalise.match(NOTE_AMPUTEE);
  return amputee !== null && amputee[1].length < 7;
}

/* -------------------------------------------------------------------------- */
/*  Le plan                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Plan pour un lot de virements émis, dans l'ordre donné.
 *
 * Le lot compte, il n'est pas un `map`. Cas réel qui l'impose : `NC-26-00455`,
 * note de 22,00 €, a reçu **deux** virements de 22,00 € à un mois d'intervalle,
 * même communication — **44,00 € sortis pour une note de 22,00 €**, et rien ne
 * l'a signalé. Le registre local fait que le second virement voit ce que le
 * premier a déjà pris, et part en vérification humaine au lieu de doubler.
 *
 * C'est le miroir exact du registre de `planBatch` (`matching.ts:222`), imposé
 * par le cas `CDV-WBK-2601408` côté entrant.
 */
export function planRefundBatch(mouvements: CodaMovement[], index: RefundIndex): RefundPlan[] {
  const engage = new Map<string, number>();

  const disponible = (n: CreditNoteRef) =>
    Math.max(0, n.amountCents - n.refundedCents - (engage.get(n.number) ?? 0));
  const reserver = (n: CreditNoteRef, cents: number) =>
    engage.set(n.number, (engage.get(n.number) ?? 0) + cents);

  return mouvements.map((m) => planRefundOne(m, index, disponible, reserver));
}

/** Plan pour un seul virement. Utile en test ; `planRefundBatch` en production. */
export function planRefund(mouvement: CodaMovement, index: RefundIndex): RefundPlan {
  const engage = new Map<string, number>();
  return planRefundOne(
    mouvement,
    index,
    (n) => Math.max(0, n.amountCents - n.refundedCents - (engage.get(n.number) ?? 0)),
    (n, c) => engage.set(n.number, (engage.get(n.number) ?? 0) + c),
  );
}

function planRefundOne(
  m: CodaMovement,
  index: RefundIndex,
  disponible: (n: CreditNoteRef) => number,
  reserver: (n: CreditNoteRef, cents: number) => void,
): RefundPlan {
  const plan: RefundPlan = {
    externalRef: m.reference,
    amountCents: m.amountCents,
    valueDate: m.valueDate,
    links: [],
    aConfirmer: [],
    leftoverCents: m.amountCents,
    manualReasons: [],
    userId: null,
  };

  const brut = m.communication.raw ?? '';

  /* --- Échelon 1 : la forme dit une dépense hors familles ------------------ */
  const hors = classifyOutgoing(brut) ?? classifyOutgoing(m.freeText);
  if (hors) {
    plan.outOfScope = hors;
    plan.manualReasons.push('out_of_scope_pattern');
    return plan;
  }

  /* --- Échelon 2 : une communication structurée, forcément la nôtre --------
     Un virement ÉMIS portant une structurée valide l'a reçue de nous. Si nous
     ne la reconnaissons pas, c'est une anomalie — l'inverse exact de
     `foreign_communication` côté entrant, où une structurée inconnue est
     simplement celle d'un autre système. La résolution viendra avec
     `refund_requests` ; d'ici là on le signale sans rien inventer. */
  if (m.communication.structuredDigits) {
    const demande = index.demandeParCommunication(m.communication.structuredDigits);
    if (demande) {
      const du = Math.max(0, demande.amountCents - demande.placedCents);
      /* Ici encore, le montant décide. Une demande de 30,00 € soldée par un
         virement de 30,00 € se place seule ; tout écart attend un humain,
         parce qu'un écart sur une demande que NOUS avons émise est une
         anomalie et non une variante. */
      if (du === m.amountCents) {
        plan.userId = demande.userId;
        plan.links.push({
          target: 'provision',
          amountCents: du,
          refundRequestId: demande.id,
          provisionUserId: demande.userId,
          method: 'structured',
        });
        plan.leftoverCents -= du;
        return plan;
      }
      plan.userId = demande.userId;
      plan.manualReasons.push('montant_discordant');
      plan.aConfirmer.push({
        target: 'provision',
        amountCents: Math.min(m.amountCents, du),
        refundRequestId: demande.id,
        provisionUserId: demande.userId,
        method: 'structured',
      });
      return plan;
    }
    plan.manualReasons.push('own_communication_unknown');
    return plan;
  }

  /* --- Ce que la communication cite --------------------------------------- */
  const numerosNotes = allCreditNoteRefs(brut);
  const refsFactures = allLegacyRefs(brut);
  const tronquee = citeUneNoteTronquee(brut);

  if (numerosNotes.length === 0 && refsFactures.length === 0 && !tronquee) {
    plan.manualReasons.push('no_reference');
    return plan;
  }

  /* --- Les notes citées, résolues ----------------------------------------- */
  const notes: CreditNoteRef[] = [];
  let uneNoteInconnue = false;
  for (const numero of numerosNotes) {
    const note = index.noteParNumero(numero);
    if (note) notes.push(note);
    else {
      uneNoteInconnue = true;
      // Le numéro est conservé, même sans note : c'est une décision
      // d'architecture, pas un repli. Il part en proposition à confirmer.
      plan.aConfirmer.push({
        target: 'credit_note',
        amountCents: m.amountCents,
        creditNoteNumber: numero,
        creditNoteId: null,
        method: 'credit_note_number',
      });
    }
  }
  if (uneNoteInconnue) plan.manualReasons.push('unknown_credit_note');

  /* --- La facture citée, résolue ------------------------------------------ */
  let facture: FactureCitee | undefined;
  for (const ref of refsFactures) {
    const f = index.factureParChiffres(ref.digits);
    if (!f) continue;
    /* Une référence dont le préfixe a été rattrapé doit prouver qu'elle
       désigne bien cette facture : son tag doit être celui du numéro trouvé.
       C'est ce contrôle, et lui seul, qui autorise à lire `NDSC-2601466`. Sans
       lui, la collision des sept chiffres suffirait. */
    if (ref.tolerated) {
      const tag = invoiceTag(f.invoiceNumber);
      if (!tag || tag !== ref.tag) continue;
    }
    facture = f;
    break;
  }
  /* Une facture citée mais introuvable n'est un problème que si RIEN d'autre
     n'a résolu. Le format annoncé cite la facture PUIS la note ; quand la note
     est là, le numéro de facture n'est qu'une corroboration, et exiger qu'il
     résolve aussi enverrait en file manuelle des remboursements parfaitement
     lisibles — dont les 24 qui citent les deux. */
  if (refsFactures.length > 0 && !facture && notes.length === 0) {
    plan.manualReasons.push('unknown_reference');
  }

  /* --- La famille, et le PIÈGE 3 ------------------------------------------ */
  const familleNote = notes.find((n) => n.userId)?.userId ?? null;
  const familleFacture = facture?.userId ?? null;

  if (familleNote && familleFacture && familleNote !== familleFacture) {
    /* Les deux références désignent deux familles. Une des deux est fausse, et
       rien ne dit laquelle. On ne choisit pas : rendre de l'argent en face de
       la mauvaise famille est la faute qu'on ne peut pas rattraper. */
    plan.manualReasons.push('references_contradictoires');
    plan.aConfirmer.push(
      ...notes.map((n) => lienDeNote(n, Math.min(m.amountCents, n.amountCents))),
      ...(facture ? [lienDeFacture(facture, m.amountCents)] : []),
    );
    return plan;
  }
  plan.userId = familleNote ?? familleFacture;

  /* --- La troncature ------------------------------------------------------
     Signalée même quand une note a été lue : un texte coupé a pu perdre une
     SECONDE note, et c'est exactement ce qui explique cinq des sept écarts de
     montant mesurés. */
  if (tronquee || m.communication.possiblyTruncated) {
    plan.manualReasons.push('note_tronquee');
  }

  /* --- L'invariant 2' : une note ne se rembourse pas deux fois ------------- */
  const servables = notes.filter((n) => disponible(n) > 0);
  if (notes.length > 0 && servables.length === 0) {
    plan.manualReasons.push('credit_note_already_refunded');
    plan.aConfirmer.push(...notes.map((n) => lienDeNote(n, Math.min(m.amountCents, n.amountCents))));
    return plan;
  }

  /* --- LA RÈGLE : le montant décide --------------------------------------
     Le virement se rapproche tout seul si, et seulement si, son montant est
     exactement celui de ce qui est cité et encore dû. Tout écart — partiel,
     groupé, arrondi, note coupée — est proposé et attend un humain.

     Cette exigence d'égalité stricte est ce qui rend le rapprochement sûr sans
     rien savoir de l'état de la facture (voir l'en-tête du module). */
  const totalServable = servables.reduce((s, n) => s + disponible(n), 0);

  if (servables.length > 0 && totalServable === m.amountCents && !tronquee
      && !plan.manualReasons.includes('note_tronquee')) {
    for (const n of servables) {
      const part = disponible(n);
      plan.links.push(lienDeNote(n, part));
      reserver(n, part);
      plan.leftoverCents -= part;
    }
    return plan;
  }

  /* --- Pas d'égalité : on propose, on n'applique pas ---------------------- */
  if (servables.length > 0) {
    plan.manualReasons.push('montant_discordant');
    plan.aConfirmer.push(
      ...servables.map((n) => lienDeNote(n, Math.min(m.amountCents, disponible(n)))),
    );
    return plan;
  }

  /* --- Aucune note, mais une facture citée --------------------------------
     Le cas des cinq remboursements qui ne citent qu'une facture. On ne peut
     pas deviner quelle note il solde : c'est une proposition. */
  if (facture) {
    plan.aConfirmer.push(lienDeFacture(facture, m.amountCents));
    if (!plan.manualReasons.includes('montant_discordant')) {
      plan.manualReasons.push('montant_discordant');
    }
  }

  return plan;
}

function lienDeNote(n: CreditNoteRef, cents: number): ProposedRefundLink {
  return {
    target: 'credit_note',
    amountCents: cents,
    creditNoteNumber: n.number,
    creditNoteId: n.id,
    invoiceId: n.invoiceId,
    invoiceNumber: n.invoiceNumber,
    method: 'credit_note_number',
  };
}

function lienDeFacture(f: FactureCitee, cents: number): ProposedRefundLink {
  return {
    target: 'invoice',
    amountCents: cents,
    invoiceId: f.id,
    invoiceNumber: f.invoiceNumber,
    method: 'legacy_reference',
  };
}

/* -------------------------------------------------------------------------- */
/*  Le compte rendu                                                            */
/* -------------------------------------------------------------------------- */

export interface RefundBatchSummary {
  virements: number;
  centimesEmis: number;
  /** Rapprochés automatiquement : le montant coïncidait. */
  rapproches: number;
  centimesRapproches: number;
  /** Proposés, en attente d'une confirmation humaine. */
  aConfirmer: number;
  /** Écartés à la forme : volontariat, salaires, frais. */
  horsPerimetre: number;
  centimesHorsPerimetre: number;
  parRaison: Record<RefundManualReason, number>;
  notesTouchees: number;
}

export function summarizeRefunds(plans: RefundPlan[]): RefundBatchSummary {
  const parRaison = {
    no_reference: 0,
    out_of_scope_pattern: 0,
    unknown_credit_note: 0,
    unknown_reference: 0,
    credit_note_already_refunded: 0,
    references_contradictoires: 0,
    montant_discordant: 0,
    note_tronquee: 0,
    own_communication_unknown: 0,
    surplus: 0,
  } as Record<RefundManualReason, number>;

  const notes = new Set<string>();
  let centimesEmis = 0, centimesRapproches = 0, centimesHors = 0;
  let rapproches = 0, aConfirmer = 0, hors = 0;

  for (const p of plans) {
    centimesEmis += p.amountCents;
    // Une raison peut apparaître deux fois dans un plan ; on compte les
    // virements concernés, pas les occurrences.
    for (const r of new Set(p.manualReasons)) parRaison[r]++;

    if (p.outOfScope) {
      hors++;
      centimesHors += p.amountCents;
      continue;
    }
    if (p.links.length > 0) {
      rapproches++;
      for (const l of p.links) {
        centimesRapproches += l.amountCents;
        if (l.creditNoteNumber) notes.add(l.creditNoteNumber);
      }
    } else if (p.aConfirmer.length > 0) {
      aConfirmer++;
    }
  }

  return {
    virements: plans.length,
    centimesEmis,
    rapproches,
    centimesRapproches,
    aConfirmer,
    horsPerimetre: hors,
    centimesHorsPerimetre: centimesHors,
    parRaison,
    notesTouchees: notes.size,
  };
}
